// 2026-09-04 신규 — 등급별 멤버십 구독료(대표 확정 2026-09-02) **멤버십 구독료 예고** 계산.
//
// 근거: `docs/ops/tiered-benefits-pricing-report.md` §17-1(확정값) · §15-1(강등 시 A안 —
// 홈에서 다음 달 예상 등급·멤버십 구독료를 한 달 내내 예고 → 1일 확정 시 멤버십 구독료가 오르는 사람만 동의 →
// 그달부터 새 멤버십 구독료. 미동의 시 청구 없이 신규 신청·다운로드 정지).
//
// 이 파일은 **표시용 계산만** 한다 — 결제·멤버십 상태·동의 저장(§17-3 4번)은 여기 없다.
// 정산 코드(`settlementCalculations.js` 등 🔴)와 무관하다: 수익 쉐어는 무변경(§12-3).
//
// 🔒 **플래그** — 요금제는 약관 v3.0 법무 검토 전(§17-3 1번)이라 실서비스에서는 꺼져 있다.
// `REACT_APP_TIER_PRICING_PREVIEW=true` 로 빌드했을 때만 화면에 멤버십 구독료 줄이 나온다.
// 데모 빌드(`scripts/build-demo-site.js`)가 이 값을 켠다. 실서비스에 켜려면 약관 시행 절차
// (7일 공지 + `user_policy_consent`)가 먼저다 — 플래그 기본값을 true 로 바꾸지 말 것.
//
// 배치(2026-09-04 대표 결정, 시안 `docs/design/tier-fee-forecast-variants.html`): **모바일 홈 = 시안 B 멤버십 구독료 열**
// (2행 카드 우측에 다음 달 금액, 3행 금지). /my-tier 는 히어로 아래 원장 카드(PC·모바일 동일).
// **PC 홈은 2026-09-07 대표 결정으로 카드 아래 원장 대신 카드 안 코칭 줄 우측 흰 알약**(`TierFeePill`,
// 시안 P1 `docs/design/tier-fee-in-card-variants.html`) — 문장은 `short`(모바일 짧은 줄과 같은 사전).
// 이 파일의 `short`·`ledger`·`mobileCol` 필드가 세 배치의 문장을 준다.
//
// 원칙(등급 카드 카탈로그와 같다 — `tierCardCopy.js`):
//   - 문장은 고정 사전에서만. "예상"이 아니라 조건문("지금 추이면" / "이대로면").
//   - 컷 수치는 여기 오지 않는다 — 훅이 준 등급명만 쓴다.
//   - 멤버십 구독료가 **오르는** 방향은 §15-1 지시대로 붉은 톤, 내리면 초록. (등급 방향 칩은 기존
//     "무게로만" 규칙 그대로 — 색은 멤버십 구독료 숫자에만 쓴다. Home.md 🔒 규칙과의 충돌은 보고서에 표시.)
import { CREATOR_TIER_NAMES } from "./creatorTierNames";
import { nextConfirmDateLabel } from "./creatorTierMetrics";
import { tt, ttl } from "../i18n/tt";
import { fmtMonth, fmtMonthDay } from "./formatLocale";
import { seg, segsFromMarked } from "./markedSegs";

// §17-1 확정 요금표 (월, VAT 포함가). 루키·휴면은 표에 없다 — 아래 함수가 따로 다룬다.
export const TIER_MONTHLY_FEE = Object.freeze({
  실버: 199000,
  골드: 99000,
  플래티넘: 55000,
  다이아몬드: 0,
  마스터: 0,
});
// 2026-09-22 대표 결정 — 등급제(등급 결정·채널 한도·루키 작품 규칙)는 기존·신규 회원 모두 **2026-10-01** 부터. 기존 회원은
// **멤버십 구독료 결제만** tier_billing_effective_from(2026-11-01) 전날까지 유예. 서버 app_config.tier_system_effective_from(RPC system_from)이
// 우선이고 없으면 이 값. 종전(2026-09-07) 「기존 회원은 10/31 까지 아무것도 적용하지 않는다」는 폐기.
export const TIER_SYSTEM_START = "2026-10-01";
// 기존 회원 첫 결제일의 화면 기본값(서버 tier_billing_effective_from 을 못 읽을 때만) — 판정에는 쓰지 않는다.
export const TIER_BILLING_START_DEFAULT = "2026-11-01";
// 🔒 가입비 모델은 2026-09-21 대표 결정이 최신이다 — 「첫 달 멤버십 구독료 포함」이 아니라 「결제일부터 한 달 이용 포함」.
export const SIGNUP_FEE = 300000; // 정식 등급 확정 시 1회 · 결제일부터 한 달 실버 이용 포함 → 그달 잔여일은 그달 확정 등급 일할 → 다음 달 1일부터 월 멤버십 구독료 (billingState.signupCoverage)
export const REACTIVATION_FEE = 150000; // 휴면·해지 후 복귀 1회. (「30일 내 재개 면제」·「복귀 시 실버 재시작」은 2026-09-07 대표 지시로 폐기 — 어디에도 쓰지 않는다)

// 주의: `process.env.REACT_APP_TIER_PRICING_PREVIEW` 는 CRA·데모 빌드(DefinePlugin)가 **이 표현식
// 그대로**를 문자열로 치환한다. `typeof process` 가드를 앞에 두면 브라우저 번들에서 process 가 없어
// 치환된 값에 닿기도 전에 false 가 된다 — 그래서 try/catch 로 감싼다.
function envFlag() {
  try {
    return process.env.REACT_APP_TIER_PRICING_PREVIEW === "true";
  } catch (e) {
    return false;
  }
}

export function isTierPricingEnabled() {
  if (envFlag()) return true;
  // 테스트·미리보기 런타임 스위치. 실서비스 번들에서는 아무도 세팅하지 않는다.
  if (typeof window !== "undefined" && window.__LAEEBLY_TIER_PRICING_PREVIEW__ === true) return true;
  return false;
}

export function monthlyFeeForTier(tierName) {
  if (!tierName || !CREATOR_TIER_NAMES.includes(tierName)) return null;
  return TIER_MONTHLY_FEE[tierName];
}

// 99000 → "99,000원", 0 → "0원"
export function formatKrw(amount) {
  if (amount === null || amount === undefined || Number.isNaN(Number(amount))) return "";
  return tt("a.f7dda0b8", "{{v0}}원", { v0: Math.round(Number(amount)).toLocaleString("ko-KR") });
}

// 0원은 "면제"로 읽힌다 — 다이아·마스터에게 "0원"보다 "면제"가 정확하다(§13-1).
export function feeLabel(amount) {
  if (amount === null || amount === undefined) return "";
  return amount === 0 ? tt("a.acd64428", "면제") : formatKrw(amount);
}

// "10월" — 다음 확정월(= 다음 달) 라벨. 날짜 문자열은 nextConfirmDateLabel 에서만 만든다.
export function nextMonthLabel(now = new Date()) {
  return fmtMonth(((now.getMonth() + 1) % 12) + 1);
}
export function thisMonthLabel(now = new Date()) {
  return fmtMonth(now.getMonth() + 1);
}
// 오늘 "YYYY-MM-DD" — 서버 date 문자열(billing_from 등)과 문자열로 비교한다. 월 라벨과 같은 로컬 날짜 기준.
function localYmd(now = new Date()) {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

// (seg·segsFromMarked 는 상단 import — 공용 `./markedSegs`)
export { segsFromMarked };
// ── 일할 계산 (2026-09-04 대표 지시) ─────────────────────────────────────────
// 등급은 매월 1일 확정되고 멤버십 구독료는 월 단위다. 월 중간에 멤버십이 시작되면(가입비 납부 후 데뷔가 아닌
// 재멤버십·복귀·수동 개시) **그 달은 결제일부터 말일까지 남은 일수만큼만** 청구하고, 다음 달 1일부터
// 확정 등급의 월 멤버십 구독료를 통째로 청구한다. 그래야 청구 주기가 등급 확정 주기(1일)에 맞는다.
//   예) 8월 15일 시작 · 골드 99,000원 → 15일~31일 17일분 = 99,000 × 17/31 = 54,290원(원 미만 내림)
// 기준일(결제일)을 포함해 센다. 1일 시작이면 전액, 말일 시작이면 1일분.
// 가입비는 일할 대상이 아니다(300,000 고정) — 결제일부터 한 달이 가입비에 포함되고, 그 뒤 그달 남은 날만 그달 확정 등급 멤버십 구독료를 일할한다
// (대표 2026-09-21 · 서버 billing_signup_coverage · 청구 _shared/charge.ts chargeUser).
// 등급 승급권(3개월)은 구매일 기준 기간제라 월 경계와 무관하며 여기 규칙을 쓰지 않는다(§24조).
export function daysInMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
}

/**
 * @param {number} monthlyFee 그 달 확정 등급의 월 멤버십 구독료
 * @param {Date} startDate 멤버십 시작(결제)일 — 이 날을 포함해 말일까지 센다
 * @returns {{ amount:number, days:number, daysInMonth:number, fraction:number, fullMonth:boolean }}
 */
export function prorateMonthlyFee(monthlyFee, startDate) {
  const fee = Number(monthlyFee);
  if (!(startDate instanceof Date) || Number.isNaN(startDate.getTime()) || Number.isNaN(fee) || fee < 0) return null;
  const dim = daysInMonth(startDate);
  const days = dim - startDate.getDate() + 1; // 시작일 포함
  const fraction = days / dim;
  const amount = Math.floor(fee * fraction); // 원 미만 내림 — 크리에이터에게 유리한 방향
  return { amount, days, daysInMonth: dim, fraction, fullMonth: days === dim };
}

// 화면 문장: "8월 15일 시작 · 17/31일분 54,290원 (골드 99,000원 기준) · 9월 1일부터 월 멤버십 구독료"
export function prorationLabel(tierName, startDate) {
  const fee = monthlyFeeForTier(tierName);
  if (fee === null) return null;
  const p = prorateMonthlyFee(fee, startDate);
  if (!p) return null;
  const m = startDate.getMonth() + 1;
  const d = startDate.getDate();
  // 날짜는 월 이름 포매터로(2026-09-07 전수 검수 — 영어 「8/15」 는 미국식 M/D 라 모호). 등급명은 표시용 번역.
  const start = fmtMonthDay(m, d);
  const next = fmtMonthDay((m % 12) + 1, 1);
  const tierText = ttl(tierName);
  if (fee === 0) return tt("a.bbd80a6a", "{{v0}} 시작 · {{v1}}는 멤버십 구독료 면제", { v0: start, v1: ttl(tierText) });
  if (p.fullMonth) return tt("a.f5c7f035", "{{v0}} 시작 · {{v1}} 전액 {{v2}} ({{v3}})", { v0: start, v1: fmtMonth(m), v2: formatKrw(fee), v3: ttl(tierText) });
  return tt("a.99510e2a", "{{v0}} 시작 · {{v1}}/{{v2}}일분 {{v3}} ({{v4}} {{v5}} 기준) · {{v6}}부터 월 멤버십 구독료", { v0: start, v1: p.days, v2: p.daysInMonth, v3: formatKrw(p.amount), v4: ttl(tierText), v5: formatKrw(fee), v6: next });
}

/**
 * 등급 요약 → 멤버십 구독료 예고 뷰모델.
 *
 * @param {object|null} summary useCreatorTierSummary 결과 (confirmedTier · dailyTier · status · isDormant)
 * @param {{ now?: Date, enabled?: boolean }} [opts] enabled 를 주면 플래그 대신 그 값을 쓴다(테스트용)
 * @returns {null | {
 *   kind: "formal"|"rookie"|"dormant",
 *   currentTier: string|null, currentFee: number|null,
 *   forecastTier: string|null, forecastFee: number|null,
 *   delta: "up"|"down"|"same"|null,      // 멤버십 구독료 방향 (up = 더 낸다)
 *   consentRequired: boolean,            // §15-1: 오르는 사람만 확정 시 동의
 *   tone: "up"|"down"|"neutral",         // 색 — up 붉게, down 초록
 *   thisMonth: string, nextMonth: string, nextConfirmDate: string,
 *   line: Array<{text,strong}>,          // 홈 데스크톱·/my-tier 한 줄
 *   short: Array<{text,strong}>,         // 홈 모바일 짧은 줄
 *   note: string|null,                   // "10월 1일 확정 시 동의 후 적용 · 예상 등급은 참고용" 등
 *   ledger: { left:{label,value}, right:{label,value,delta}|null, note:string }, // PC 원장 카드(시안 C)
 *   mobileCol: { label, value, sub },   // 모바일 홈 우측 멤버십 구독료 열(시안 B)
 * }}
 */

// 2026-09-05 — 등급제 시행 일정(대표 승인): 기존 회원은 청구 시작일 전날까지 **멤버십 구독료만** 유예. 시작일은 서버 app_config
// tier_billing_effective_from 에서 온다 — 2026-09-06 개정으로 2026-11-01 00:00 KST(유예 10/31 까지, 종전 12/1). 화면 코드에 날짜 없음.
// RPC get_tier_billing_state 의 jsonb 를 화면용 객체로. 서버 규칙: docs/ops/channel-limit-rollout-plan.md §2-2 ·
// 마이그레이션 20260905190000. 호출처 useTierBillingState → buildFeeForecast({ billing }).
export function normalizeTierBillingState(raw) {
  if (!raw || typeof raw !== "object") return null;
  return {
    tier: raw.tier ?? null,
    status: raw.status ?? null,
    dormant: Boolean(raw.dormant),
    monthlyFee: Number(raw.monthly_fee ?? 0) || 0,
    exempt: Boolean(raw.exempt),
    isNewUser: Boolean(raw.is_new_user),
    memberSince: raw.member_since ?? null,
    effectiveFrom: raw.effective_from ?? null,
    billingFrom: raw.billing_from ?? null,
    grace: Boolean(raw.grace),
    billable: Boolean(raw.billable),
    reason: raw.reason ?? null,
    // 2026-09-06 — 루키 승급 심사 승인 달 일할(마이그레이션 20260906150000). 구버전 서버는 키가 없어 기본값.
    fullMonthlyFee: Number(raw.full_monthly_fee ?? raw.monthly_fee ?? 0) || 0,
    debutAt: raw.debut_at ?? null,
    prorated: Boolean(raw.prorated),
    proratedDays: raw.prorated_days === undefined || raw.prorated_days === null ? null : Number(raw.prorated_days),
    daysInMonth: raw.days_in_month === undefined || raw.days_in_month === null ? null : Number(raw.days_in_month),
    // 2026-10-06 — 추천 가입 혜택(가입 유형 「멤버십 구독료 면제 N개월」 · 마이그레이션 20261018110000). 구버전 서버는 키가 없어 기본값.
    //   prorated_reason: 'debut'(승급 심사 승인 달 일할) | 'promo_end'(면제가 이달 중간에 끝나 그 뒤 일할). prorated_from = 일할 시작일.
    proratedFrom: raw.prorated_from ?? null,
    proratedReason: raw.prorated_reason ?? null,
    promo: Boolean(raw.promo),
    promoSince: raw.promo_since ?? null,
    promoUntil: raw.promo_until ?? null,
    promoMonths: raw.promo_months === undefined || raw.promo_months === null ? null : Number(raw.promo_months),
    promoPending: Boolean(raw.promo_pending),
  };
}

// "2026-01-05" + 1 → "2026-01-06" (UTC 날짜 연산 — 서버 date 문자열만, 타임존 변환 없음)
export function addYmd(iso, days) {
  const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + days)).toISOString().slice(0, 10);
}
function daysInYmdMonth(iso) {
  const m = String(iso || "").match(/^(\d{4})-(\d{2})/);
  if (!m) return null;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]), 0)).getUTCDate();
}

// "2026-11-01" → "11월 1일" / "11월". 서버 date 문자열만 받는다(타임존 변환 없음).
export function billingDateLabel(iso, { monthOnly = false } = {}) {
  if (!iso || typeof iso !== "string") return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const month = Number(m[2]);
  const day = Number(m[3]);
  return monthOnly ? fmtMonth(month) : fmtMonthDay(month, day);
}

export function buildFeeForecast(summary, { now = new Date(), enabled, billing = null } = {}) {
  const on = enabled === undefined ? isTierPricingEnabled() : enabled;
  if (!on || !summary) return null;

  const thisMonth = thisMonthLabel(now);
  const nextMonth = nextMonthLabel(now);
  // 이 예고를 만드는 시각 기준 — 등급 카드(buildTierView)와 같은 날짜(요약에 굳은 날짜를 쓰면 자정 뒤 열어 둔 탭에서 두 날짜가 갈렸다 · 131차 #3)
  const nextConfirmDate = nextConfirmDateLabel(now);
  const base = {
    currentTier: null,
    currentFee: null,
    forecastTier: null,
    forecastFee: null,
    delta: null,
    consentRequired: false,
    tone: "neutral",
    thisMonth,
    nextMonth,
    nextConfirmDate,
    note: null,
  };

  // 휴면 — 계정 휴면(6개월 협업 0건)과 등급 휴면(90일)은 다르지만(§11-4), 화면에 멤버십 구독료를 청구
  // 중이라고 쓰면 안 되는 건 같다. 복귀 비용만 알린다.
  if (summary.isDormant) {
    // 2026-10-13 대표 결정 — 기존 회원은 청구 시작일(11/1) 전날까지 돌아오면 재활성화비 면제(시안 C 「복귀 유도형」).
    // 서버는 휴면을 유예보다 먼저 판정해 grace=false 로 주므로, 기존 회원 여부와 청구 시작일로 여기서 가른다.
    const waiveUntil = billing && !billing.isNewUser && billing.billingFrom && localYmd(now) < billing.billingFrom
      ? billingDateLabel(addYmd(billing.billingFrom, -1)) : null;
    if (waiveUntil) {
      return {
        ...base,
        kind: "dormant",
        reactivationWaived: true,
        line: [seg(tt("a.dw.lead", "기존 회원 혜택 ·") + " "), seg(tt("a.dw.line", "{{v0}}까지는 재활성화비 없이 다시 시작할 수 있어요", { v0: waiveUntil }), true)],
        short: [seg(tt("a.dw.lead", "기존 회원 혜택 ·") + " "), seg(tt("a.dw.short", "{{v0}}까지 무료 복귀", { v0: waiveUntil }), true)],
        ledger: {
          left: { label: tt("a.abc3d0d0", "멤버십 구독료"), value: tt("a.c4ea1311", "청구 정지") },
          right: { label: tt("a.dw.right", "복귀 시 재활성화비 · 기존 회원 혜택"), value: formatKrw(0), delta: null },
          note: tt("a.dw.note", "{{v0}}부터는 돌아올 때 재활성화비 {{v1}}이 1회 있어요. 그 전에 돌아오면 내지 않아요.", { v0: billingDateLabel(billing.billingFrom), v1: formatKrw(REACTIVATION_FEE) }),
        },
        mobileCol: { label: tt("a.13f7b92a", "멤버십 구독료"), value: tt("a.c4ea1311", "청구 정지"), sub: tt("a.dw.short", "{{v0}}까지 무료 복귀", { v0: waiveUntil }) },
      };
    }
    return {
      ...base,
      kind: "dormant",
      line: [seg(tt("a.537bb915", "휴면 중에는 멤버십 구독료가 청구되지 않아요 · 복귀 시 재활성화비") + " "), seg(formatKrw(REACTIVATION_FEE), true)],
      short: [seg(tt("a.dbe33b48", "멤버십 구독료 청구 없음 · 복귀 시") + " "), seg(formatKrw(REACTIVATION_FEE), true)],
      ledger: {
        left: { label: tt("a.abc3d0d0", "멤버십 구독료"), value: tt("a.c4ea1311", "청구 정지") },
        right: { label: tt("a.2690dc8d", "복귀 시 재활성화비"), value: formatKrw(REACTIVATION_FEE), delta: null },
        note: tt("a.1b9959e4", "휴면 중에는 청구되지 않아요 · 복귀할 때 재활성화비가 1회 있어요"),
      },
      mobileCol: { label: tt("a.13f7b92a", "멤버십 구독료"), value: tt("a.c4ea1311", "청구 정지"), sub: tt("a.36e07073", "휴면 중") },
    };
  }

  // 루키·승인 직후 — 결제 전 루키 구간(§14-3). 5편 채워 데뷔할 때 가입비.
  const isRookieLike = summary.status === "rookie" || summary.status === "pre-rookie" || !summary.confirmedTier;
  if (isRookieLike) {
    // 추천 가입 혜택 대기(가입 유형 「멤버십 구독료 면제 N개월」 · 아직 루키) — 정식 등급이 되면 그날부터 N개월 0원. 가입비는 같은 가입 유형이 면제하는 경우가 대부분이라 여기서 말하지 않는다.
    const pm = billing && billing.promoPending && billing.promoMonths ? billing.promoMonths : null;
    if (pm) {
      return {
        ...base,
        kind: "rookie",
        currentFee: 0,
        promoPending: true,
        promoMonths: pm,
        line: [seg(tt("a.883b9cba", "루키 구간은 멤버십 구독료가 없어요 · 정식 등급이 되면 추천 가입 혜택으로") + " "), seg(tt("a.f381990a", "{{v0}}개월 멤버십 구독료 없음", { v0: pm }), true)],
        short: [seg(tt("a.1a3647a1", "멤버십 구독료 없음 · 정식 등급이 되면") + " "), seg(tt("a.0f7d2fac", "{{v0}}개월 무료", { v0: pm }), true)],
        ledger: {
          left: { label: tt("a.7f8ca81a", "루키 구간 멤버십 구독료"), value: tt("a.a245e6fe", "없음") },
          right: { label: tt("a.9b8bf717", "정식 등급이 되면 · 추천 가입 혜택"), value: tt("a.f381990a", "{{v0}}개월 멤버십 구독료 없음", { v0: pm }), delta: null },
          note: tt("a.a1800d41", "추천 코드로 가입해 정식 등급이 된 날부터 {{v0}}개월 동안 멤버십 구독료가 없어요 · 그 뒤 그달 남은 날은 날짜만큼, 다음 달부터 월 멤버십 구독료", { v0: pm }),
        },
        mobileCol: { label: tt("a.13f7b92a", "멤버십 구독료"), value: tt("a.a245e6fe", "없음"), sub: tt("a.5d3dc321", "정식 등급 뒤 {{v0}}개월 무료", { v0: pm }) },
      };
    }
    return {
      ...base,
      kind: "rookie",
      currentFee: 0,
      line: [seg(tt("a.1227d542", "루키 구간은 멤버십 구독료가 없어요 · 정식 등급이 되면 가입비") + " "), seg(formatKrw(SIGNUP_FEE), true), seg(" " + tt("a.86694c5e", "(결제일부터 한 달 이용 포함)"))],
      short: [seg(tt("a.9462f98c", "멤버십 구독료 없음 · 데뷔 때 가입비") + " "), seg(formatKrw(SIGNUP_FEE), true)],
      ledger: {
        left: { label: tt("a.7f8ca81a", "루키 구간 멤버십 구독료"), value: tt("a.a245e6fe", "없음") },
        right: { label: tt("a.ee8d102e", "정식 등급이 되면 가입비"), value: formatKrw(SIGNUP_FEE), delta: null },
        note: tt("a.5f6ca6d3", "가입비에는 결제일부터 한 달 이용이 포함돼요 · 그달 남은 날은 날짜만큼만, 다음 달 1일부터 매월 확정 등급의 멤버십 구독료"),
      },
      mobileCol: { label: tt("a.13f7b92a", "멤버십 구독료"), value: tt("a.a245e6fe", "없음"), sub: tt("a.2a85a6b7", "루키 구간") },
    };
  }

  const currentTier = summary.confirmedTier;
  const forecastTier = summary.dailyTier || currentTier;
  const currentFee = monthlyFeeForTier(currentTier);
  const forecastFee = monthlyFeeForTier(forecastTier);
  if (currentFee === null || forecastFee === null) return null;

  // 추천 가입 혜택 — 가입 유형 「멤버십 구독료 면제 N개월」(2026-10-06 대표 결정 · 서버 tier_billing_state.reason='promo'). 정식 등급 확정일부터
  // 일 단위로 N개월 − 1일까지 0원. 끝나는 날이 월 중간이면 그 다음 날부터 말일까지 일할(가입비 포함 기간의 잔여일과 같은 규칙) → 알약에 그 금액을 같이 보인다.
  // 화면 이름은 「추천 가입」 — 가입 유형 이름(협업처)은 보이지 않는다(signup-cohort-plan D8).
  if (billing && billing.reason === "promo" && billing.promoUntil) {
    const until = billing.promoUntil;
    const resumesOn = addYmd(until, 1);
    const resumeDay = Number(resumesOn.slice(8, 10));
    const dim = daysInYmdMonth(resumesOn);
    const remaining = dim - resumeDay + 1;
    const resumeFee = resumeDay === 1 ? forecastFee : Math.floor((forecastFee * remaining) / dim);
    const resumeLabel = resumeDay === 1 ? billingDateLabel(resumesOn, { monthOnly: true }) : billingDateLabel(resumesOn);
    const untilLabel = billingDateLabel(until);
    const sinceLabel = billingDateLabel(billing.promoSince) || "";
    const months = billing.promoMonths || "";
    const fc0 = feeLabel(resumeFee);
    const daysText = resumeDay === 1 ? "" : ` (${tt("a.8ceb847e", "{{v0}}일분", { v0: remaining })})`;
    const sameTier = forecastTier === currentTier;
    const promoLine = tt("a.7eda8da4", "추천 가입 · {{v0}}까지 · {{v1}}부터", { v0: untilLabel, v1: resumeLabel });
    return {
      ...base,
      kind: "promo",
      currentTier,
      currentFee: 0,
      forecastTier,
      forecastFee,
      delta: "same",
      consentRequired: false,
      tone: "neutral",
      promoSince: billing.promoSince,
      promoUntil: until,
      promoMonths: billing.promoMonths,
      resumesOn,
      resumeFee,
      line: [
        seg(tt("a.42ac4f63", "{{v0}} 멤버십 구독료", { v0: thisMonth }) + " "), seg(tt("a.4ed20aeb", "0원"), true), seg(" · " + promoLine + " "), seg(fc0, true), seg(daysText),
        ...(sameTier ? [] : [seg(" " + tt("a.3103dc98", "(지금 추이면 {{v0}})", { v0: ttl(forecastTier) }))]),
      ],
      short: [seg(tt("a.4ed20aeb", "0원"), true), seg(" · " + promoLine + " "), seg(fc0, true), seg(daysText)],
      note: tt("a.045aac90", "추천 코드로 가입해 정식 등급이 된 {{v0}}부터 {{v1}}개월 동안 멤버십 구독료가 없어요 · {{v2}}까지 0원 · 그 뒤 그달 남은 날은 날짜만큼, 다음 달부터 월 멤버십 구독료", { v0: sinceLabel, v1: months, v2: untilLabel }),
      ledger: {
        left: { label: tt("a.0bb226f2", "{{v0}} 멤버십 구독료 · {{v1}}", { v0: thisMonth, v1: ttl(currentTier) }), value: tt("a.cd414816", "0원 · 추천 가입") },
        right: {
          label: sameTier ? tt("a.dc471038", "{{v0}}부터 · {{v1}}", { v0: resumeLabel, v1: ttl(currentTier) }) : tt("a.6501f82a", "{{v0}}부터 · 지금 추이면 {{v1}}", { v0: resumeLabel, v1: ttl(forecastTier) }),
          value: `${fc0}${daysText}`,
          delta: null,
        },
        note: tt("a.3816a364", "추천 가입 혜택 · {{v0}}까지 멤버십 구독료 0원 · {{v1}}부터 그달 남은 날은 날짜만큼, 다음 달부터 그달 확정 등급의 월 멤버십 구독료 · 채널 한도 등 나머지 규칙은 그대로", { v0: untilLabel, v1: billingDateLabel(resumesOn) }),
      },
      mobileCol: { label: tt("a.b9e3bb48", "{{v0}}부터", { v0: resumeLabel }), value: fc0, sub: tt("a.c56d6f3b", "추천 가입 · 지금 0원") },
    };
  }

  // 유예(기존 회원, 청구 시작일 전) — 이번 달 0원, 시작일부터 그달 확정 등급의 멤버십 구독료. 방향 색·동의는 붙이지 않는다(§15-1 은
  // 청구가 시작된 뒤의 규칙). 시작일 라벨을 못 읽으면 유예 문구 없이 기존 예고로 떨어진다.
  const graceFrom = billing && billing.grace && !billing.exempt ? billingDateLabel(billing.billingFrom) : null;
  if (graceFrom) {
    const fc0 = feeLabel(forecastFee);
    const sameTier = forecastTier === currentTier;
    const fromMonth = billingDateLabel(billing.billingFrom, { monthOnly: true });
    return {
      ...base,
      kind: "grace",
      currentTier,
      currentFee: 0,
      forecastTier,
      forecastFee,
      delta: "same",
      consentRequired: false,
      tone: "neutral",
      billingFrom: billing.billingFrom,
      line: [
        seg(tt("a.42ac4f63", "{{v0}} 멤버십 구독료", { v0: thisMonth }) + " "), seg(tt("a.4ed20aeb", "0원"), true), seg(" " + tt("a.39f0fa87", "· 유예 중 · {{v0}}부터", { v0: graceFrom }) + " "), seg(fc0, true),
        ...(sameTier ? [] : [seg(" " + tt("a.3103dc98", "(지금 추이면 {{v0}})", { v0: ttl(forecastTier) }))]),
      ],
      short: [seg(tt("a.59358490", "유예 중 ·") + " "), seg(tt("a.b9e3bb48", "{{v0}}부터", { v0: graceFrom }) + " "), seg(fc0, true)],
      note: tt("a.4b109a8d", "기존 회원은 {{v0}} 전날까지 멤버십 구독료가 없어요 · 청구 금액은 그달 확정 등급으로 정해져요", { v0: graceFrom }),
      ledger: {
        left: { label: tt("a.0bb226f2", "{{v0}} 멤버십 구독료 · {{v1}}", { v0: thisMonth, v1: ttl(currentTier) }), value: tt("a.7a04f706", "0원 · 유예") },
        right: { label: sameTier ? tt("a.dc471038", "{{v0}}부터 · {{v1}}", { v0: graceFrom, v1: ttl(currentTier) }) : tt("a.6501f82a", "{{v0}}부터 · 지금 추이면 {{v1}}", { v0: graceFrom, v1: ttl(forecastTier) }), value: fc0, delta: null },
        note: tt("a.e96c561d", "기존 회원은 {{v0}} 0시까지 멤버십 구독료가 없어요 · 그날부터 그달 확정 등급의 멤버십 구독료가 청구돼요 · 채널 한도 등 나머지 규칙은 유예 없이 적용", { v0: graceFrom }),
      },
      mobileCol: { label: tt("a.b9e3bb48", "{{v0}}부터", { v0: fromMonth }), value: fc0, sub: tt("a.3c2ceba5", "지금은 유예") },
    };
  }

  // 데뷔 달 일할(2026-09-06 대표 결정 ②) — 루키 승급 심사가 승인된 달은 확정일부터 말일까지만 청구(서버 tier_billing_state.prorated).
  // 유예·면제가 아니고 실제 청구 대상(billable)일 때만. 다음 달 1일부터는 확정 등급의 월 멤버십 구독료 전액.
  // 일할: 데뷔(승급 심사 승인일부터) 또는 추천 가입 혜택 종료(마지막 무료일 다음 날부터) — 서버 prorated_reason 으로 가른다. 구버전 서버는 debut_at 만.
  const promoEnd = Boolean(billing && billing.proratedReason === "promo_end");
  const debutFrom = billing && billing.prorated && billing.billable && !billing.exempt ? billingDateLabel(billing.proratedFrom || billing.debutAt) : null;
  if (debutFrom && billing.proratedDays && billing.daysInMonth) {
    const full = billing.fullMonthlyFee || currentFee;
    const part = formatKrw(billing.monthlyFee);
    const fc0 = feeLabel(forecastFee);
    const sameTier = forecastTier === currentTier;
    const promoUntilLabel = promoEnd ? billingDateLabel(billing.promoUntil || addYmd(billing.proratedFrom, -1)) : null;
    return {
      ...base,
      kind: "debut",
      proratedReason: promoEnd ? "promo_end" : "debut",
      currentTier,
      currentFee: billing.monthlyFee,
      forecastTier,
      forecastFee,
      delta: "same",
      consentRequired: false,
      tone: "neutral",
      debutAt: billing.debutAt,
      proratedFrom: billing.proratedFrom || billing.debutAt,
      line: [
        seg(tt("a.42ac4f63", "{{v0}} 멤버십 구독료", { v0: thisMonth }) + " "), seg(part, true),
        promoEnd
          ? seg(" " + tt("a.29125b52", "· 추천 가입 혜택 {{v0}}까지 · {{v1}}부터 {{v2}}/{{v3}}일분 ({{v4}} {{v5}} 기준) · {{v6}}부터", { v0: promoUntilLabel, v1: debutFrom, v2: billing.proratedDays, v3: billing.daysInMonth, v4: ttl(currentTier), v5: formatKrw(full), v6: nextMonth }) + " ")
          : seg(" " + tt("a.f70edf86", "· {{v0}} 실버 확정 · {{v1}}/{{v2}}일분 ({{v3}} {{v4}} 기준) · {{v5}}부터", { v0: debutFrom, v1: billing.proratedDays, v2: billing.daysInMonth, v3: ttl(currentTier), v4: formatKrw(full), v5: nextMonth }) + " "),
        seg(fc0, true),
        ...(sameTier ? [] : [seg(" " + tt("a.3103dc98", "(지금 추이면 {{v0}})", { v0: ttl(forecastTier) }))]),
      ],
      short: [seg(`${thisMonth} `), seg(part, true), seg(" " + tt("a.9239f469", "· {{v0}}일분 · {{v1}}부터", { v0: billing.proratedDays, v1: nextMonth }) + " "), seg(fc0, true)],
      note: promoEnd
        ? tt("a.7800c2f6", "추천 가입 혜택이 {{v0}}에 끝나 {{v1}}부터 말일까지 일할 계산한 금액이에요 · {{v2}} 1일부터 그달 확정 등급의 월 멤버십 구독료", { v0: promoUntilLabel, v1: debutFrom, v2: nextMonth })
        : tt("a.b2f58aab", "{{v0}}부터 말일까지 일할 계산한 금액이에요 · {{v1}} 1일부터 그달 확정 등급의 월 멤버십 구독료", { v0: debutFrom, v1: nextMonth }),
      ledger: {
        left: { label: tt("a.ac0f744d", "{{v0}} 멤버십 구독료 · {{v1}} · {{v2}}/{{v3}}일분", { v0: thisMonth, v1: ttl(currentTier), v2: billing.proratedDays, v3: billing.daysInMonth }), value: part },
        right: { label: sameTier ? tt("a.dc471038", "{{v0}}부터 · {{v1}}", { v0: nextMonth, v1: ttl(currentTier) }) : tt("a.6501f82a", "{{v0}}부터 · 지금 추이면 {{v1}}", { v0: nextMonth, v1: ttl(forecastTier) }), value: fc0, delta: null },
        note: promoEnd
          ? tt("a.70c185d1", "추천 가입 혜택 {{v0}}까지 · 다음 날부터 말일까지 일할 계산(원 미만 내림) · 다음 달부터 월 멤버십 구독료 전액", { v0: promoUntilLabel })
          : tt("a.90016854", "{{v0}} 실버 확정 · 확정일부터 말일까지 일할 계산(원 미만 내림) · 다음 달부터 월 멤버십 구독료 전액", { v0: debutFrom }),
      },
      mobileCol: { label: tt("a.42ac4f63", "{{v0}} 멤버십 구독료", { v0: thisMonth }), value: part, sub: tt("a.8ceb847e", "{{v0}}일분", { v0: billing.proratedDays }) },
    };
  }

  const delta = forecastFee > currentFee ? "up" : forecastFee < currentFee ? "down" : "same";
  const consentRequired = delta === "up";
  const tone = delta === "up" ? "up" : delta === "down" ? "down" : "neutral";
  const cur = feeLabel(currentFee);
  const fc = feeLabel(forecastFee);

  let line;
  let short;
  let note = null;
  // 문장은 사전 한 키(굵은 자리 **…**) — 조각 이어 붙이기는 영어 어순에서 깨진다(「9 Waived · 10 too Waived」, 2026-09-07)
  const tierText = ttl(forecastTier);
  if (delta === "same") {
    line = segsFromMarked(tt("a.a79934ac", "{{v0}} 멤버십 구독료 **{{v1}}** · 지금 추이면 {{v2}}도 **{{v3}}**", { v0: thisMonth, v1: cur, v2: nextMonth, v3: fc }));
    short = segsFromMarked(tt("a.54ba7e2f", "{{v0}} **{{v1}}** · {{v2}}도 **{{v3}}**", { v0: thisMonth, v1: cur, v2: nextMonth, v3: fc }));
  } else if (delta === "up") {
    line = segsFromMarked(tt("a.f7fce80f", "{{v0}} 멤버십 구독료 **{{v1}}** · 지금 추이면 {{v2}} 등급 {{v3}}, 멤버십 구독료 **{{v4}}**", { v0: thisMonth, v1: cur, v2: nextMonth, v3: ttl(tierText), v4: fc }));
    short = [seg(`${thisMonth} `), seg(cur, true), seg(` → ${nextMonth} `), seg(fc, true)];
    note = `${nextConfirmDate} 확정 때 동의한 뒤 적용돼요 · 예상 등급은 참고용`;
  } else {
    line = segsFromMarked(tt("a.7f8d3cfb", "{{v0}} 멤버십 구독료 **{{v1}}** · 이대로면 {{v2}} 등급 {{v3}}, 멤버십 구독료 **{{v4}}**", { v0: thisMonth, v1: cur, v2: nextMonth, v3: ttl(tierText), v4: fc }));
    short = [seg(`${thisMonth} `), seg(cur, true), seg(` → ${nextMonth} `), seg(fc, true)];
    note = "내려가는 멤버십 구독료는 동의 없이 바로 적용돼요";
  }

  const diff = forecastFee - currentFee;
  const deltaText = diff === 0 ? null : `${diff > 0 ? "+" : "−"}${formatKrw(Math.abs(diff))}`;
  const ledger = {
    left: { label: tt("a.0bb226f2", "{{v0}} 멤버십 구독료 · {{v1}}", { v0: thisMonth, v1: ttl(currentTier) }), value: cur },
    right: {
      label: delta === "same" ? tt("a.d0513f0a", "{{v0}} 예상 · 지금 추이면 {{v1}}", { v0: nextMonth, v1: ttl(forecastTier) }) : delta === "up" ? tt("a.d0513f0a", "{{v0}} 예상 · 지금 추이면 {{v1}}", { v0: nextMonth, v1: ttl(forecastTier) }) : tt("a.7a10f226", "{{v0}} 예상 · 이대로면 {{v1}}", { v0: nextMonth, v1: ttl(forecastTier) }),
      value: fc,
      delta: deltaText,
    },
    note: delta === "up" ? tt("a.c583a8da", "{{v0}} 확정 때 동의를 받아요 · 예상 등급은 참고용", { v0: nextConfirmDate }) : delta === "down" ? tt("a.1a05af9a", "내려가는 멤버십 구독료는 동의 없이 바로 적용돼요") : tt("a.416bef9b", "다음 등급 결정 {{v0}} · 예상 등급은 참고용", { v0: nextConfirmDate }),
  };
  const mobileCol = {
    label: tt("a.42ac4f63", "{{v0}} 멤버십 구독료", { v0: nextMonth }),
    value: fc,
    sub: delta === "same" ? (forecastFee === 0 ? tt("a.f01a2d80", "다이아몬드 이상 면제") : tt("a.77f5e04b", "변동 없음")) : tt("a.a2040e70", "지금 {{v0}}", { v0: cur }),
  };

  return {
    ...base,
    kind: "formal",
    currentTier,
    currentFee,
    forecastTier,
    forecastFee,
    delta,
    consentRequired,
    tone,
    line,
    short,
    note,
    ledger,
    mobileCol,
  };
}
